import { LinkOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { Modal, message } from "antd";
import React, { useState } from "react";
import { ADMIN_GUIDES } from "../../admin/pages/help/helpGuides";

// Tutorials shown to instructors. Titles and descriptions come from the admin
// guides; the videos are instructor cuts (login-as-admin intro removed).
const INSTRUCTOR_VIDEOS = {
  "guide-class-announcements": "instructor-class-announcements",
  "guide-student-attendance": "instructor-student-attendance",
  "guide-report-card": "instructor-report-card",
  "guide-course-board": "instructor-course-board",
  "guide-schedule": "instructor-schedule",
  "guide-exam": "instructor-exam",
};

const GUIDES = Object.entries(INSTRUCTOR_VIDEOS)
  .map(([id, file]) => {
    const guide = ADMIN_GUIDES.find((g) => g._id === id);
    return guide && { ...guide, video: `/assets/help-videos/${file}.mp4` };
  })
  .filter(Boolean);

// Instructor-only tutorials (no matching admin guide)
GUIDES.push(
  {
    _id: "guide-messages",
    question: "Messages",
    answer: "Send a message to a student's parent.",
    details:
      "Log into the Is-Kool portal and open the menu.\nUnder Communication, select Message.\nThe Messages page shows the chat history. Click Create New Message.\nIn the message form, select the class and then the student. The parent name is shown.\nType your message in the message box and click Send.\nYou can send more messages to the parent in the same way.",
    video: "/assets/help-videos/instructor-messages.mp4",
  },
  {
    _id: "guide-homework",
    question: "Homework",
    answer: "Send homework to a class.",
    details:
      "Log into the Is-Kool portal and open the menu.\nUnder Academic, select Homework.\nSelect the class. The class broadcast opens with the subjects on the left and the earlier messages.\nThe subject is set to All Subjects by default, or pick a particular subject.\nType the homework message in the message box. You can also attach a file or an image.\nClick Send. A message confirms that it was sent successfully.",
    video: "/assets/help-videos/instructor-homework.mp4",
  }
);

const toLines = (text) =>
  (text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

export default function HelpPage() {
  const [playingId, setPlayingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const playing = GUIDES.find((g) => g._id === playingId);

  const toggleMore = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const copyLink = (video) => {
    navigator.clipboard
      .writeText(window.location.origin + video)
      .then(() => message.success("Video link copied"))
      .catch(() => message.error("Could not copy link"));
  };

  return (
    <div className="instructor-help p-4 sm:p-8">
      <style>{`
        .instructor-help .yt-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, 130px);
          gap: 14px 10px;
        }
        .instructor-help .yt-card { cursor: pointer; }
        .instructor-help .yt-thumb {
          position: relative;
          aspect-ratio: 16 / 9;
          border-radius: 6px;
          overflow: hidden;
          background: #063C4E;
        }
        .instructor-help .yt-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
          transition: transform .25s;
        }
        .instructor-help .yt-card:hover .yt-thumb video { transform: scale(1.04); }
        .instructor-help .yt-thumb-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 18px;
          background: rgba(0, 0, 0, .18);
          opacity: 0;
          transition: opacity .2s;
        }
        .instructor-help .yt-card:hover .yt-thumb-play { opacity: 1; }
        .instructor-help .yt-title {
          font-size: 11px;
          font-weight: 600;
          color: #111827;
          line-height: 1.35;
        }
        .instructor-help .yt-desc {
          font-size: 10px;
          color: #6b7280;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .instructor-help .yt-more {
          font-size: 10px;
          font-weight: 500;
          color: #09617E;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
        }
        .instructor-help .yt-more:hover { text-decoration: underline; }
        .instructor-help .yt-steps {
          margin: 4px 0 0;
          padding-left: 16px;
          font-size: 10px;
          color: #4b5563;
          line-height: 1.5;
        }
        .instructor-help .yt-steps li + li { margin-top: 2px; }
        .instructor-help .yt-player {
          width: 100%;
          aspect-ratio: 16 / 9;
          display: block;
          background: #000;
          border-radius: 12px;
        }
        @media (max-width: 480px) {
          .instructor-help .yt-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px 10px;
            justify-content: stretch;
          }
        }
      `}</style>
      <div className="bg-white p-4 sm:p-6 rounded-2xl space-y-6">
        <div className="yt-grid">
          {GUIDES.map((guide) => (
            <div
              key={guide._id}
              className="yt-card space-y-2"
              onClick={() => setPlayingId(guide._id)}
            >
              <div className="yt-thumb">
                <video muted preload="metadata" src={`${guide.video}#t=1`} />
                <div className="yt-thumb-play">
                  <PlayCircleOutlined />
                </div>
              </div>
              <div className="min-w-0">
                <div className="yt-title">{guide.question}</div>
                <div className="yt-desc">{guide.answer}</div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    className="yt-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMore(guide._id);
                    }}
                  >
                    {expanded[guide._id] ? "Show less" : "View more"}
                  </button>
                  <button
                    type="button"
                    className="yt-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLink(guide.video);
                    }}
                  >
                    <LinkOutlined /> Copy link
                  </button>
                </div>
                {expanded[guide._id] && (
                  <ol className="yt-steps" onClick={(e) => e.stopPropagation()}>
                    {toLines(guide.details || guide.answer).map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl text-orange-500 text-medium">
              Contact Info
            </div>
            <div className="text-gray-500 ">Phone: +91 95913 45370</div>
            <div className="text-gray-500 ">
              Email Id: contactus@twinbotz.com
            </div>
          </div>
        </div>
      </div>

      <Modal
        width={900}
        open={!!playing}
        onCancel={() => setPlayingId(null)}
        footer={null}
        destroyOnClose
      >
        {playing && (
          <div className="space-y-3 pt-6">
            <video
              controls
              preload="metadata"
              className="yt-player"
              src={playing.video}
            />
            <div className="yt-title">{playing.question}</div>
            <div className="text-gray-600">{playing.answer}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
